/*
 *  Author: Raul Perusquia <raul@inikoo.com>
 *  Created: Sun, 23 Oct 2022 09:30:38 British Summer Time, Sheffield, UK
 *  Copyright (c) 2022, Raul A Perusquia Flores
 */

import { defineStore } from "pinia"
import type { Language } from "@/types/Locale"
import { ref } from "vue"


// Pinia Store
export const useLocaleStore = defineStore("locale", () => {
	const locale_iso = ref<string | null>(null)   // 'en-GB'
	const language = ref<Language>({
		id: 68,
		code: "en",
		name: "English",
	})
	const languageOptions = ref<Language[]>([language.value])
	const languageAssetsOptions = ref<Language[]>([language.value])
	const currencyInertia = ref({})

	const number = (number: number) => {
		return new Intl.NumberFormat(locale_iso.value || language.value.code).format(number)
	}

	// Derive the locale from the currency's home country so the amount is laid out
	// the way that country writes it (e.g. RON -> ro-RO -> "80,09 lei", not "lei 80.09").
	// The first two letters of an ISO 4217 currency code are the ISO 3166 country code.
	// Digits stay Latin (-u-nu-latn): NPR -> ne-NP would otherwise print "रु १४०.००".
	const localeForCurrency = (currencyCode: string): string | undefined => {
		if (!currencyCode) {
			return locale_iso.value || language.value.code
		}
		try {
			const region = currencyCode.slice(0, 2).toUpperCase()
			const maximized = new Intl.Locale("und", { region }).maximize()
			return `${maximized.language}-${region}-u-nu-latn`
		} catch {
			return locale_iso.value || language.value.code
		}
	}

	// ponytail: mirrors the master shop price_exchanges fraction_digits config, keep in sync
	const WHOLE_NUMBER_PRACTICE_CURRENCIES = ["CZK", "HUF", "UAH", "SEK", "IDR"]

	// RRP is a recommended shelf price, so in whole-number countries it is rounded up
	// for display; stored values and exact unit rates (price/units) keep their decimals.
	const currencyFormatRrp = (currencyCode: string, amount: number | string): string | number => {
		const num = typeof amount === "string" ? parseFloat(amount) : (amount || 0)
		const resolvedCurrency = currencyInertia.value?.code || currencyCode || ''

		return currencyFormat(currencyCode, WHOLE_NUMBER_PRACTICE_CURRENCIES.includes(resolvedCurrency) ? Math.ceil(num) : num)
	}

	const currencyFormat = (currencyCode: string, amount: number | string): string | number => {
		// if (typeof amount === "undefined" || amount === null) return 0
		const getAmount = amount ?? 0
		if (!currencyCode) {
			return getAmount || 0
		}

		const num = typeof getAmount === "string" ? parseFloat(getAmount) : (getAmount || 0)
		const resolvedCurrency = currencyInertia.value?.code || currencyCode || ''

		// Retail practice in these countries is whole-number prices, so a whole amount
		// shows without ".00" while totals with real decimals still show them.
		const wholeNumberPractice = WHOLE_NUMBER_PRACTICE_CURRENCIES.includes(resolvedCurrency) && Number.isInteger(num)

		const formatter = new Intl.NumberFormat(localeForCurrency(resolvedCurrency), {
			style: resolvedCurrency ? "currency" : "decimal",
			currency: resolvedCurrency,
			minimumFractionDigits: wholeNumberPractice ? 0 : 2,
			maximumFractionDigits: 2,
			currencyDisplay: "narrowSymbol",  // to make UAH -> ₴, USD -> $, etc.
		})

		return formatter.format(num);
	};

	const currencyFormatUnitCost = (currencyCode: string | null | undefined, amount: number | string | null | undefined): string => {
		const num = typeof amount === "string" ? parseFloat(amount) : (amount ?? 0)

		return new Intl.NumberFormat(currencyCode ? localeForCurrency(currencyCode) : locale_iso.value || language.value.code, {
			style: currencyCode ? "currency" : "decimal",
			currency: currencyCode || undefined,
			minimumFractionDigits: 2,
			maximumFractionDigits: 4,
			currencyDisplay: "narrowSymbol",
		}).format(num)
	}

	const unitCostInputOptions = (currencyCode: string | null | undefined) => {
		if (!currencyCode || !/^[A-Z]{3}$/i.test(currencyCode)) {
			return { minFractionDigits: 0, maxFractionDigits: 4 }
		}

		return {
			mode: "currency",
			currency: currencyCode.toUpperCase(),
			locale: localeForCurrency(currencyCode.toUpperCase()),
			currencyDisplay: "narrowSymbol",
			minFractionDigits: 2,
			maxFractionDigits: 4,
		}
	}

	const currencyInputOptions = (currencyCode: string | null | undefined) => {
		if (!currencyCode) {
			return {}
		}

		return {
			mode: "currency",
			currency: currencyCode,
			locale: localeForCurrency(currencyCode),
			currencyDisplay: "narrowSymbol",
			minFractionDigits: WHOLE_NUMBER_PRACTICE_CURRENCIES.includes(currencyCode) ? 0 : 2,
			maxFractionDigits: 2,
		}
	}

	const currencySymbolNarrow = (currencyCode: string) => {
		const resolvedCurrency = currencyInertia.value?.code || currencyCode || ''

		const formatter = new Intl.NumberFormat(localeForCurrency(resolvedCurrency), {
			style: resolvedCurrency ? "currency" : "decimal",
			currency: resolvedCurrency,
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
			currencyDisplay: "narrowSymbol",  // to make UAH -> ₴, USD -> $, etc.
		})

		return formatter.formatToParts(1).find(part => part.type === 'currency')?.value ?? ''
	}

	const numberShort = (number: number) => {
		return new Intl.NumberFormat(locale_iso.value || language.value.code, {
			notation: "compact",
			compactDisplay: "short",
			maximumFractionDigits: 1,
		}).format(number)
	}


	const currencySymbol = (currencyCode: string) => {
		if(!currencyCode) return '-'

		return new Intl.NumberFormat('en', {
			style: 'currency',
			currency: currencyCode,
			currencyDisplay: 'symbol'
		}).formatToParts(123).find(part => part.type === 'currency')?.value ?? '';
	}

	const CurrencyShort = (currencyCode: string, number: number) => {

			let formattedNumber = new Intl.NumberFormat("en", {
				notation: "compact",
				compactDisplay: "short",
				style: "currency",
				currency: currencyCode,
			}).format(number);

			formattedNumber = formattedNumber.replace(/(\d)([KMGTPE])/g, (match, p1, p2) => {
				return `${p1}${p2}`;
			});

			return formattedNumber;

	}

	return { language, locale_iso, languageOptions, number, numberShort, currencySymbolNarrow, currencyFormat, currencyFormatRrp, currencyFormatUnitCost, unitCostInputOptions, currencyInputOptions, CurrencyShort, currencySymbol, languageAssetsOptions  }
})
